import { View, ViewManager } from './ViewManager';
import { addTouchHandlesForMouse } from './utils';
import { LogFactory, LogLevel } from '../../src-ts/common/Log';
import { ControllerState } from './ControllerState';
import { GameSession } from './GameSession';
import { JoinCode } from '../../src-ts/common/messages/JoinCode';
import KeyboardShortcuts from './KeyboardShortcuts';
import { CookieHelper } from './CookieHelper';
import { ControllerActionButton } from './ControllerActionButton';
import { CustomControllerData } from './CustomControllerData';
import { ImageURLCache } from './ImageURLCache';
import { HungKludge } from './HungKludge';
import { DeviceId } from '../../src-ts/common/messages/DeviceId';
import { CoOperationHelpInfoType, HelpInfo } from './HelpInfo';

export class App {
	/** public to allow HTML to access. */
	public readonly viewManager = new ViewManager();
	public readonly cookieHelper: CookieHelper = new CookieHelper();

	private gameSessions: GameSession[] = [];
	private _activeGameSessionInView!: GameSession;
	private keyboardController: KeyboardShortcuts;

	private _mostRecentControllerState: ControllerState;
	public get mostRecentControllerState(): ControllerState {
		return this._mostRecentControllerState;
	}

	/**
	 * Once 1 player has joined, this is the join code they used to connect,
	 * and hence the one that all subsequent players on this device must use
	 */
	private forceJoinCode?: JoinCode;

	public get resettingApp(): boolean {
		return this._resettingApp;
	}
	private _resettingApp: boolean = false;

	public get _sessionCount(): number {
		return this.gameSessions.length;
	}

	public static readonly MaxGameSessions: number = 8;

	public static readonly DefaultTurnsPerRound = 4;
	public static readonly MinValidTurnsPerRound = 1;
	public static readonly MaxValidTurnsPerRound = 8;

	private awayAutoReadying: boolean = false;

	private joinCodeVisible: boolean = true;

	private controllerData!: CustomControllerData;

	public readonly imageURLCache: ImageURLCache = new ImageURLCache();

	private readonly hungKludge: HungKludge;

	private writerButtonsScrolling: boolean = false;
	private writerButtonsScrollTimeout: NodeJS.Timeout | undefined;

	public static readonly AllDirectionNames: string[] = ['north', 'east', 'south', 'west'];

	// Temporary hardcoded number of actions, in the future this could be moddable
	public static readonly ActionButtonCount: number = 4;

	constructor() {
		// TODO: TEMP
		LogFactory.setDefaultDefaultLevelAndAllExistingLevels(LogLevel.All);

		this.viewManager.hideAll();

		addTouchHandlesForMouse();

		this._mostRecentControllerState = ControllerState.JoinScreen;

		// Default controller data for CO OPERATION (initialises this.controllerData)
		this.resetControllerDataToDefault();

		// Add initial GameSession, or restore from cookiess
		this.resetApp();

		this.keyboardController = new KeyboardShortcuts(this);

		// Loss/gain of focus (modern version)
		document.addEventListener('visibilitychange', this.onVisibilityChange);

		// Loss/Gain of focus (old version = paranoia)
		document.addEventListener('blur', this.onBlur);
		document.addEventListener('focus', this.onFocus);

		// // Intercept the "Reload" button
		// window.addEventListener('beforeunload', this.onBeforeUnload, { capture: true });

		// // Do not rely on 'unload' event = often not fired (especially on mobile browsers)
		// window.addEventListener('unload', this.onUnload);

		// Inisialise join code visibility to saved value (or true by default)
		this.joinCodeVisible = this.cookieHelper.getJoinCodeVisible();
		this.viewManager.updateJoinCodeToggles(this.joinCodeVisible);

		this.hungKludge = new HungKludge();
	}

	public isActiveGameSessionInView = (gameSession: GameSession): boolean => {
		return gameSession === this.activeGameSessionInView;
	};

	public get activeGameSessionInView(): GameSession {
		return this._activeGameSessionInView;
	}

	public get allJoinedPlayerNames(): string[] {
		// filter defined playerNames only
		return this.gameSessions.flatMap((s) => (s.playerName ? [s.playerName] : []));
	}

	public get allSelectedCharacterIndexes(): number[] {
		return this.gameSessions.flatMap((s) => s.selectedCharacterIndex);
	}

	public setCustomControllerActionButton = (buttonIndex: number, actionButton: ControllerActionButton): void => {
		this.controllerData.setActionButton(buttonIndex, actionButton);
		// Hide Co OPERATION-specific help buttons when using custom controls
		this.viewManager.setHelpButtonsShowing(false);
	};
	public resetControllerDataToDefault = (): void => {
		this.controllerData = CustomControllerData.getDefaultControllerData();
		// Only show Co OPERATION-specific help buttons when using default controls
		this.viewManager.setHelpButtonsShowing(true);
	};

	public getActionButtonData = (index: number): ControllerActionButton => {
		if (index < 0 || index >= App.ActionButtonCount) {
			throw new Error(
				`Invalid index (${index}) when trying to get action button data, expected between 0 and ${
					App.ActionButtonCount - 1
				}`
			);
		}

		if (this.controllerData.actionButtons && index < this.controllerData.actionButtons.length) {
			return this.controllerData.actionButtons[index];
		}
		return CustomControllerData.getDefaultControllerData().actionButtons![index];
	};

	public getActionNumberByName = (actionName: string, expectSuccesss: boolean = true): number => {
		const defaultControllerData = CustomControllerData.getDefaultControllerData();
		for (let i = 0; i < App.ActionButtonCount; i++) {
			let actionButtonData;
			if (this.controllerData.actionButtons && i < this.controllerData.actionButtons.length) {
				actionButtonData = this.controllerData.actionButtons[i];
			} else {
				actionButtonData = defaultControllerData.actionButtons![i];
			}
			if (actionButtonData.action === actionName) {
				return i;
			}
		}
		if (expectSuccesss) {
			throw new Error(
				`Unable to find ${ControllerActionButton.name} with action name '${actionName}' when trying to get action number`
			);
		} else {
			Log.debug(
				`Unable to find ${ControllerActionButton.name} with action name '${actionName}' when trying to get action number (not expecting success)`
			);
			return -1;
		}
	};

	// noinspection JSUnusedGlobalSymbols -- (used by HTML)
	public consumeEvent = (evt: Event): void => {
		/*evt = evt || window.event; -- latter is deprecated! */
		if ('undefined' == typeof evt.stopPropagation) {
			evt.cancelBubble = true;
		} else {
			evt.stopPropagation();
		}
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onReloadButtonPressed = (): void => {
		location.reload();
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onMenuButtonPressed = (): void => {
		this.buttonPressVibration();
		this.viewManager.showGameMenu();
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onJoinButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.JoinScreen) {
			Log.warn(
				`Ignoring join button press because not in ${ControllerState[ControllerState.JoinScreen]} state, is ${
					ControllerState[this.mostRecentControllerState]
				}`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'joinButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				// Use join code from HTML input field by default.  Priority order:
				// 1. From `forceJoinCode` (set by successful join by a session on this controller)
				// 2. From UI (when button pressed)
				// 3. From cookie.
				const joinParams = this.viewManager.tryGetJoinCodeAndPlayerNameFromHTML();
				if (!joinParams.joinCode) {
					const joinCodeFromCookie = this.cookieHelper.getJoinCode();
					if (joinCodeFromCookie) joinParams.joinCode = joinCodeFromCookie;
				}
				joinParams.forceJoinCode = this.forceJoinCode;
				const result = await this.activeGameSessionInView.joinGame(joinParams);

				if (result.success) {
					this.forceJoinCode = result.joinCode;
					this.cookieHelper.setJoinCode(result.joinCode!);
					this.updateSessionsCookie();
				}
				// else wrong join code.
				// A dialog is shown elsewhere which, when dismissed, refreshes the join screen.
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onCancelJoinButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		await this.awaitButtonPressIfNotAlready(
			'cancelJoinButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				// Ends/closes connection of the most recent GameSession, which is
				//	expected to be the activeGameSessionInView when the Cancel button is pressed
				if (!this.isActiveGameSessionInView(this.gameSessions[this.gameSessions.length - 1])) {
					Log.error(
						'Expected activeGameSessionInView to be the most recent GameSession when calling onCancelJoinButtonPressed, but it was not'
					);
					return;
				}

				if (await this.tryEndGameSessionAtIndex(this.gameSessions.length - 1)) {
					// Session ended, show WaitOrAddPlayers view with updated player names
					this.viewManager.setupJoinedPlayersList(this.allJoinedPlayerNames, this._sessionCount);
					this.viewManager.show(View.WaitOrAddPlayers);
				} else {
					// TODO-2022/08/16: Await ignored.  Should we restructure slightly?
					this.viewManager.showInfoPopupDialog('Error', 'Failed to cancel joining game.', null);
				}
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onAreaOutsideMenuPressed = (): void => {
		this.buttonPressVibration();
		this.activeGameSessionInView.onCancelOrBack();
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onJoinCodeToggleButtonPressed = (): void => {
		this.buttonPressVibration();
		this.joinCodeVisible = !this.joinCodeVisible;
		this.cookieHelper.setJoinCodeVisible(this.joinCodeVisible);
		this.viewManager.updateJoinCodeToggles(this.joinCodeVisible);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onPlayerTabButtonPressed = async (tabIndex: number): Promise<void> => {
		this.buttonPressVibration();
		if (await this.trySwitchActiveGameSession(tabIndex)) {
			Log.log(`Selecting player tab: ${tabIndex}`);
		}
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onSelectCharacterButtonPressed = async (
		pressedElement: HTMLElement,
		characterIndex: number
	): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.CharacterSelect) {
			Log.warn(
				`Ignoring select character button press because not in ${
					ControllerState[ControllerState.CharacterSelect]
				} state`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'selectCharacterButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				await this.activeGameSessionInView.requestCharacterSelection(characterIndex);
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onSelectNonCharacterButtonPressed = async (
		pressedElement: HTMLElement,
		characterIndex: number
	): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.CharacterSelect) {
			Log.warn(
				`Ignoring select non-character button press because not in ${
					ControllerState[ControllerState.CharacterSelect]
				} state`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'selectCharacterButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				await this.activeGameSessionInView.requestNonCharacterSelection();
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onAddPlayerButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.JoinScreen) {
			Log.warn(`Ignoring add player button press because not in ${ControllerState[ControllerState.JoinScreen]} state`);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'addPlayerButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				this.viewManager.setConnectingInfoToDisplay(1, 1);
				// Add player on this device: Try to add a new GameSession & make it the active one
				if (await this.tryAddGameSession()) {
					await this.trySwitchActiveGameSession(this.gameSessions.length - 1);
				}
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onRemovePlayerButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.JoinScreen) {
			Log.warn(
				`Ignoring remove player button press because not in ${ControllerState[ControllerState.JoinScreen]} state`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'removePlayerButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				// The pressedElement is the button that triggered this function call, and
				//	its parent is the playerNameElement (which contains the button and text showing the player's name)
				const playerNameElement = pressedElement.parentNode;
				if (!playerNameElement) throw new Error('Failed to get parent node of pressedElement');
				if (!playerNameElement.parentNode) throw new Error('Failed to get parent node of playerNameElement');

				// Get the index of the playerNameElement as a child of its parent
				//	Players are listed in order of sessionIndex, so we can get that from this
				const elementIndex: number = Array.prototype.indexOf.call(
					playerNameElement.parentNode.childNodes,
					playerNameElement
				);
				// The first playerNameElement is not the first child, so we need to
				//	subtract to account for that and get a 0-based session index
				const sessionIndex: number = elementIndex - 2;

				if (sessionIndex < 0 || sessionIndex >= App.MaxGameSessions) {
					throw new Error(`Unexpected (invalid) session index when trying to remove player: ${sessionIndex}`);
				}

				// Remove player by ending the GameSession
				if (!(await this.tryEndGameSessionAtIndex(sessionIndex, true))) {
					await this.viewManager.showInfoPopupDialog('Error', 'Failed to remove player.', null);
				}
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onHelpButtonPressed = (helpTextOrType: string, titleText: string = ''): void => {
		let infoTextToDisplay: string = helpTextOrType;
		let titleTextToDisplay: string = titleText;

		const coOpHelpInfo: CoOperationHelpInfoType | undefined = (<any>CoOperationHelpInfoType)[helpTextOrType];
		if (coOpHelpInfo !== undefined) {
			let coOpInfoTextAndTitle = HelpInfo.getCoOperationHelpInfo(
				coOpHelpInfo,
				this.activeGameSessionInView.turnsPerRound
			);
			infoTextToDisplay = coOpInfoTextAndTitle.infoText;
			titleTextToDisplay = coOpInfoTextAndTitle.titleText;
		}

		this.viewManager.showHelpPanel(infoTextToDisplay, titleTextToDisplay);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onSelectTurnButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.awayAutoReadying) {
			Log.warn('Ignoring select turn button press because awayAutoReadying');
			return;
		}
		if (!pressedElement.parentNode) {
			return;
		}
		const elementIndex: number = Array.prototype.indexOf.call(pressedElement.parentNode.childNodes, pressedElement);
		const turnNumIndex = elementIndex;
		await this.awaitButtonPressIfNotAlready(
			'selectTurnButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.activeGameSessionInView.selectATurnNumber(turnNumIndex);
				this.buttonPressVibration();
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onSelectActionButtonPressed = async (
		pressedElement: HTMLElement | null,
		actionNumber: number
	): Promise<void> => {
		if (this.awayAutoReadying) {
			Log.warn('Ignoring select action button press because awayAutoReadying');
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'selectActionButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				if (actionNumber < 0 || actionNumber >= App.ActionButtonCount) {
					Log.log(`Trying to select action with invalid action number: ${actionNumber}`);
				}
				this.buttonPressVibration();
				await this.activeGameSessionInView.selectActionNumber(actionNumber, true);
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onSelectDataButtonPressed = async (pressedElement: HTMLElement | null, data: string): Promise<void> => {
		if (this.awayAutoReadying) {
			Log.warn('Ignoring select data button press because awayAutoReadying');
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'selectDataButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				await this.activeGameSessionInView.selectDataForCurrentTurn(data, true);
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onReadyButtonPressed = async (pressedElement: HTMLElement | null): Promise<void> => {
		if (this.awayAutoReadying) {
			Log.warn('Ignoring ready button press because awayAutoReadying');
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'readyButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				try {
					await this.activeGameSessionInView.onReadyButtonPressed(true);
				} catch (e) {
					this.viewManager.showInfoPopupDialog(
						'Error',
						`Something went wrong and the game might not know that you're ready.
						Try refreshing the page.`,
						null
					);
					throw e;
				}
			}
		);
	};

	// noinspection JSUnusedGlobalSymbols -- Used by HTML
	public onUnreadyButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.awayAutoReadying) {
			Log.warn('Ignoring unready button press because awayAutoReadying');
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'unreadyButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				try {
					await this.activeGameSessionInView.onReadyButtonPressed(false);
				} catch (e) {
					this.viewManager.showInfoPopupDialog(
						'Error',
						`Something went wrong and the game might not know that you've cancelled your actions.
						Try refreshing the page.`,
						null
					);
					throw e;
				}
			}
		);
	};

	public onAwayButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (
			this.mostRecentControllerState !== ControllerState.WaitingForInput &&
			this.mostRecentControllerState !== ControllerState.ItemPlacementVote
		) {
			Log.warn(
				`onAwayButtonPressed: Doing nothing in ${
					this.mostRecentControllerState ? ControllerState[this.mostRecentControllerState] : 'undefined'
				} state`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'awayButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				let autoReadyAttemptCount: number = 0;
				let autoReadySuccessCount: number = 0;
				for (const gameSession of this.gameSessions) {
					if (!gameSession.hasJoinedGame) {
						continue;
					}
					autoReadyAttemptCount++;
					try {
						await gameSession.trySendAutoReady(true);
						autoReadySuccessCount++;
					} catch (e) {
						Log.error(`Failed to send auto-ready enable messsage for session ${gameSession.sessionIndex}: ${e}`);
					}
				}
				if (autoReadySuccessCount >= autoReadyAttemptCount) {
					this.awayAutoReadying = true;
					this.viewManager.setAwayOverlayShowing(true);
				} else {
					Log.error(
						`Tried to send ${autoReadyAttemptCount} auto-ready enable messages to game, but only ${autoReadySuccessCount} succeeded`
					);
					this.viewManager.showInfoPopupDialog(
						'Error',
						`Something went wrong and the game might not know that you're away.
						Try refreshing the page.`,
						null
					);
				}
			}
		);
	};

	public onBackFromAwayButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (
			this.mostRecentControllerState !== ControllerState.WaitingForInput &&
			this.mostRecentControllerState !== ControllerState.ItemPlacementVote
		) {
			Log.warn(
				`onBackFromAwayButtonPressed: Doing nothing in ${
					this.mostRecentControllerState ? ControllerState[this.mostRecentControllerState] : 'undefined'
				} state`
			);
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'backFromAwayButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				let autoReadyDisableAttemptCount: number = 0;
				let autoReadyDisableSuccessCount: number = 0;
				for (const gameSession of this.gameSessions) {
					if (!gameSession.hasJoinedGame) {
						continue;
					}
					autoReadyDisableAttemptCount++;
					try {
						await gameSession.trySendAutoReady(false);
						autoReadyDisableSuccessCount++;
					} catch (e) {
						Log.error(`Failed to send auto-ready disable messsage for session ${gameSession.sessionIndex}: ${e}`);
					}
				}
				if (autoReadyDisableSuccessCount > 0) {
					this.awayAutoReadying = false;
					this.viewManager.setAwayOverlayShowing(false);
				}
				if (autoReadyDisableSuccessCount < autoReadyDisableAttemptCount) {
					Log.error(
						`Tried to send ${autoReadyDisableAttemptCount} auto-ready disable messages to game, but only ${autoReadyDisableSuccessCount} succeeded`
					);
					this.viewManager.showInfoPopupDialog(
						'Error',
						`Something went wrong and the game might not know that you're back.
						Try refreshing the page.`,
						null
					);
				}
			}
		);
	};

	public onItemVoteLeftButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.ItemPlacementVote) {
			Log.warn(
				`Ignoring item vote left button press because not in ${
					ControllerState[ControllerState.ItemPlacementVote]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.itemManagementData) {
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'itemVoteLeftButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				this.activeGameSessionInView.itemManagementData?.decrementSelectedItemVoteIndex();
				this.activeGameSessionInView.updateItemPlacementVoteVisuals();
			}
		);
	};
	public onItemVoteRightButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.ItemPlacementVote) {
			Log.warn(
				`Ignoring item vote right button press because not in ${
					ControllerState[ControllerState.ItemPlacementVote]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.itemManagementData) {
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'itemVoteRightButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				this.activeGameSessionInView.itemManagementData?.incrementSelectedItemVoteIndex();
				this.activeGameSessionInView.updateItemPlacementVoteVisuals();
			}
		);
	};
	public onItemPositionButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.ItemPlacementVote) {
			Log.warn(
				`Ignoring item vote position button press because not in ${
					ControllerState[ControllerState.ItemPlacementVote]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.itemManagementData) {
			return;
		}
		if (!pressedElement.parentNode) {
			return;
		}

		const elementIndex: number = Array.prototype.indexOf.call(pressedElement.parentNode.childNodes, pressedElement);
		const positionIndex = elementIndex - 2;

		await this.awaitButtonPressIfNotAlready(
			'itemPositionButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				const itemManagementData = this.activeGameSessionInView.itemManagementData;
				const newSelection = itemManagementData?.selectPositionForCurrentItem(
					positionIndex,
					this.activeGameSessionInView.sessionIndex
				);
				if (newSelection?.[0]) {
					// Unready when selection is changed/toggled
					this.activeGameSessionInView.onReadyButtonPressed(false);
				}
				this.activeGameSessionInView.updateItemPlacementVoteVisuals();
				if (newSelection?.[1]) {
					// If a position was chosen, display the next item that does not already have a position set
					await itemManagementData?.autoSelectNextItemWithoutPositionAfterDelay();
					this.activeGameSessionInView.updateItemPlacementVoteVisuals();
				}
			}
		);
	};

	public onWriterCharacterButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.WaitingForInput) {
			Log.warn(
				`Ignoring writer character position button press because not in ${
					ControllerState[ControllerState.WaitingForInput]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.isNonCharacterPlayer) {
			Log.debug('onWriterCharacterButtonPressed, but active session is not a writer, so doing nothing');
			return;
		}
		if (this.writerButtonsScrolling) {
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'writerCharacterButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				this.viewManager.scrollElementIntoViewCentered(pressedElement);
			}
		);
	};

	public onWriterCharacterLeftButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.WaitingForInput) {
			Log.warn(
				`Ignoring writer character left button press because not in ${
					ControllerState[ControllerState.WaitingForInput]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.isNonCharacterPlayer) {
			Log.debug('onWriterCharacterLeftButtonPressed, but active session is not a writer, so doing nothing');
			return;
		}
		const characterWritingData = this.activeGameSessionInView.characterWritingData;
		if (characterWritingData === undefined) {
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'writerCharacterLeftButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				characterWritingData.decrementSelectedCharacterIndex();
				this.viewManager.updateWriterTextInputSelectionVisuals(characterWritingData, true, false);
			}
		);
	};
	public onWriterCharacterRightButtonPressed = async (pressedElement: HTMLElement): Promise<void> => {
		if (this.mostRecentControllerState !== ControllerState.WaitingForInput) {
			Log.warn(
				`Ignoring writer character right button press because not in ${
					ControllerState[ControllerState.WaitingForInput]
				} state`
			);
			return;
		}
		if (!this.activeGameSessionInView.isNonCharacterPlayer) {
			Log.debug('onWriterCharacterRightButtonPressed, but active session is not a writer, so doing nothing');
			return;
		}
		const characterWritingData = this.activeGameSessionInView.characterWritingData;
		if (characterWritingData === undefined) {
			return;
		}
		await this.awaitButtonPressIfNotAlready(
			'writerCharacterRightButton',
			pressedElement,
			this.activeGameSessionInView.deviceId,
			async (): Promise<void> => {
				this.buttonPressVibration();
				characterWritingData.incrementSelectedCharacterIndex();
				this.viewManager.updateWriterTextInputSelectionVisuals(characterWritingData, true, false);
			}
		);
	};

	public onWriterCharacterSelectScroll = async (scrollElement: HTMLElement): Promise<void> => {
		this.writerButtonsScrolling = true;
		const centremost = this.viewManager.getCentremostElementInScrollView(scrollElement);
		this.viewManager.updateWriterCharacterScrollButtonsStyling(centremost.element);

		// Set a timeout to run writerCharacterScrollEnd after scrolling ends, first clearing any existing one
		// (There's currently no good/fully supported built-in event for onScrollEnd)
		if (this.writerButtonsScrollTimeout) {
			window.clearTimeout(this.writerButtonsScrollTimeout);
		}
		this.writerButtonsScrollTimeout = setTimeout(
			() => this.onWriterCharacterSelectScrollEnd(centremost.elementIndex),
			200
		);
	};

	public onWriterTextInputChanged = (textAreaElement: HTMLTextAreaElement): void => {
		if (!this.activeGameSessionInView.isNonCharacterPlayer) {
			Log.debug('onWriterTextInputChanged, but active session is not a writer, so doing nothing');
			return;
		}
		if (this.activeGameSessionInView.characterWritingData === undefined) {
			return;
		}
		const characterWritingData = this.activeGameSessionInView.characterWritingData;
		characterWritingData.setTextInputForSelectedCharacter(textAreaElement.value);
		textAreaElement.value = characterWritingData.selectedCharacterText;
		this.viewManager.updateWriterTextInputSelectionVisuals(characterWritingData, false);

		const textForAllCharacters = this.activeGameSessionInView.characterWritingData.getCharacterNameToTextMap();
		this.cookieHelper.setCharacterTextForSessionIndex(this.activeGameSessionInView.sessionIndex, textForAllCharacters);
	};

	public actUponControllerStateChange = async (state: ControllerState): Promise<void> => {
		const enteringExisingState = state === this.mostRecentControllerState;
		this._mostRecentControllerState = state;

		if (!enteringExisingState) {
			this.hungKludge.stopTimerAndHide();
		}

		this.keyboardController.setActive(false);
		let view: View | undefined;

		if (this.activeGameSessionInView === undefined) {
			await this.trySwitchActiveGameSession(0);
		}

		switch (state) {
			case ControllerState.JoinScreen:
				this.awayAutoReadying = false;
				view = View.WaitOrAddPlayers;
				break;

			case ControllerState.WaitingForSetup: {
				// End any GameSessions that did not join before setup
				//await this.endGameSessionsThatHaveNotJoined();
				if (!this.resettingApp && this.activeGameSessionInView.sessionIndex !== 0) {
					await this.trySwitchActiveGameSession(0);
				}
				view = View.LookAtScreen;
				if (!enteringExisingState) {
					this.hungKludge.startTimer();
				}
				break;
			}

			case ControllerState.CharacterSelect:
				view = View.CharacterSelect;
				break;

			case ControllerState.WaitingForInput: {
				// Default to the first GameSession when inputting actions
				if (!this.resettingApp && this.activeGameSessionInView.sessionIndex !== 0) {
					await this.trySwitchActiveGameSession(0);
				}
				this.keyboardController.setActive(true);
				if (this.activeGameSessionInView.hasSelectedCharacter) {
					if (this.activeGameSessionInView.isNonCharacterPlayer) {
						view = View.WriterTextInput;
					} else {
						view = View.GameInputPhase;
					}
				} else {
					Log.error(
						`no character on WaitingForInput state for active session: ${this.activeGameSessionInView.sessionIndex} (${this.activeGameSessionInView.playerName})`
					);
					// Active session did not yet select a character (joined mid-game?),
					//	so don't show any kind of input screen
					view = View.LookAtScreen;
				}
				break;
			}

			case ControllerState.ItemPlacementVote:
				// Default to the first GameSession when inputting votes
				if (!this.resettingApp && this.activeGameSessionInView.sessionIndex !== 0) {
					await this.trySwitchActiveGameSession(0);
				}
				if (this.activeGameSessionInView.hasSelectedCharacter) {
					view = View.ItemPlacementVote;
				} else {
					// Active session did not yet select a character (joined mid-game?),
					//	so don't show voting input screen
					view = View.LookAtScreen;
				}
				break;

			case ControllerState.WatchingActions:
			case ControllerState.ItemPlacementResults:
			case ControllerState.LevelFinished: // TODO-20221105: Maybe better feedback for users?
				view = View.LookAtScreen;
				if (!enteringExisingState) {
					this.hungKludge.startTimer();
				}
				break;

			default:
				throw new Error(`Unhandled controller state:${state}`);
		}

		if (
			!enteringExisingState &&
			this.awayAutoReadying &&
			(state == ControllerState.WaitingForInput || state == ControllerState.ItemPlacementVote)
		) {
			this.viewManager.setAwayOverlayShowing(true);
		} else {
			this.viewManager.setAwayOverlayShowing(false);
		}

		Log.log(`App actUponControllerStateChange: ${ControllerState[state]}`);
		if (view === undefined) {
			Log.log(`App actUponControllerStateChange: no View to show`);
			// No new view to show
			return;
		}
		if (this.resettingApp) {
			Log.log(`App actUponControllerStateChange: Ressetting game so not showing view "${view}"`);
			// The view will get updated based on controller state after resetting is completed, so skip for now
			return;
		}
		Log.log(`App actUponControllerStateChange: Showing view "${view}"`);
		this.viewManager.show(view);
	};

	/**
	 * Shows the default (splash) view, ends any existing game sessions,
	 * then adds an initial one ready to allow the player to join a game,
	 * or tries to restore previous ones from cookies
	 */
	public resetApp = async (): Promise<void> => {
		if (this.resettingApp) {
			Log.warn(`Calling resetApp() while already resetting`);
			return;
		}

		this._resettingApp = true;

		this.awayAutoReadying = false;

		this.viewManager.setDisconnectedOverlayShowing(false);
		this.viewManager.show(View.Default);

		// Reset forceJoinCode in case it was set previously
		this.forceJoinCode = undefined;

		try {
			// Ensure no active GameSessions
			await this.endAllExistingGameSessions();
		} catch (e) {
			Log.error(`Error while trying to endAllExistingGameSessions() in resetApp(): ${e}`);
		}

		// Force reset to default state
		if (this.mostRecentControllerState !== ControllerState.JoinScreen) {
			this.actUponControllerStateChange(ControllerState.JoinScreen);
		}

		// Pause briefly while the Default/splash screen is being displayed.
		// This (plus the other delay below) also reduces reconnect issues by giving the game/server enough time to
		//	finish handling any (temporary) disconnections that may have just occured if the page was refreshed.
		// Without the delay, there are more frequent sparodic cases where a device is marked as
		//	disconnected while the game is in the process of handling its reconnection.
		await new Promise((resolve) => setTimeout(resolve, 800));

		this.cookieHelper.preventSettingSessionsCookie();

		this.gameSessions = [];

		let restoredSessions: boolean = false;
		try {
			let numPreviousSessionsAdded = 0;
			const previousSessionIndicesAndNames = this.cookieHelper.getSessionsList();
			if (undefined !== previousSessionIndicesAndNames) {
				// also true when empty
				Log.debug('Found previous sessions = recreating');
				// (See above comment re. delay)
				await new Promise((resolve) => setTimeout(resolve, 700));
				for (const previousSessionIndexAndName of previousSessionIndicesAndNames) {
					this.viewManager.setConnectingInfoToDisplay(1, previousSessionIndicesAndNames.length);
					if (await this.tryAddGameSession(previousSessionIndexAndName[0], previousSessionIndexAndName[1])) {
						numPreviousSessionsAdded++;
					}
				}
				restoredSessions = true;
				if (numPreviousSessionsAdded < previousSessionIndicesAndNames.length) {
					Log.warn(
						`Only reconnected ${numPreviousSessionsAdded} out of ${previousSessionIndicesAndNames.length} previous sessions`
					);
				}
			}
		} catch (e) {
			Log.error(`Error while trying to restore sessions in resetApp(): ${e}`);
		}

		this.cookieHelper.allowSettingSessionsCookie();

		try {
			// After restoring previous sessions, we only want to keep the ones that have joined game
			// 	(some may have been booted since we were last trying to join)
			await this.endGameSessionsThatHaveNotJoined(true, true);
		} catch (e) {
			Log.error(`Error while trying to endGameSessionsThatHaveNotJoined() in resetApp(): ${e}`);
		}

		// (Not as an `else` to above in case all previous sessions fail,
		//	or there were no restored sessions that had joined a game)
		if (0 === this._sessionCount) {
			// Add initial game session
			Log.debug('No previous sessions = creating first');
			this.viewManager.setConnectingInfoToDisplay(1, 1);
			if (!(await this.tryAddGameSession())) {
				this._resettingApp = false;
				throw new Error(`Unable to add initial ${GameSession} in resetApp()`);
			}
		}

		await this.trySwitchActiveGameSession(this._sessionCount - 1);

		this._resettingApp = false;

		if (restoredSessions && this.mostRecentControllerState !== ControllerState.JoinScreen) {
			// Force actUponControllerStateChange, since view is not updated on state change while in the process of restoring sessions
			await this.actUponControllerStateChange(this.mostRecentControllerState);
		}
	};

	public switchToNextNonReadyGameSession = async (currentSessionIndex: number): Promise<void> => {
		// Ignore (don't auto-switch to) non-character players when waiting for action input
		let ignoreNonCharacterPlayers = this.mostRecentControllerState === ControllerState.WaitingForInput;

		let nextNonReadySession: number | undefined;
		for (let i = 0; i < this.gameSessions.length; i++) {
			if (
				!this.gameSessions[i].playerIsReady &&
				(!this.gameSessions[i].isNonCharacterPlayer || !ignoreNonCharacterPlayers)
			) {
				// Found an GameSession where the player is not 'ready'
				if (i > currentSessionIndex) {
					// The session comes after the current one, let's switch to it
					nextNonReadySession = i;
					break;
				} else if (nextNonReadySession === undefined) {
					// This is the first non-ready session that was found, it will be used
					//	if none are found with a session index greater than the current one
					//	(We favour picking a session that comes after the current one, otherwise we loop back around)
					nextNonReadySession = i;
				}
			}
		}
		if (nextNonReadySession !== undefined) {
			Log.log(`Switching to the next non-ready GameSession: ${nextNonReadySession}`);
			await this.trySwitchActiveGameSession(nextNonReadySession);
		} else {
			Log.log(`All players are 'ready', so not switching to another GameSession`);
		}
	};

	private trySwitchActiveGameSession = async (sessionIndex: number): Promise<boolean> => {
		if (sessionIndex < 0 || sessionIndex >= this.gameSessions.length) {
			Log.error(`switchActiveGameSession called with invalid sessionIndex: ${sessionIndex}`);
			return false;
		}

		let alreadyActive: boolean = this.isActiveGameSessionInView(this.gameSessions[sessionIndex]);
		Log.log(`Switching active GameSession: ${sessionIndex} (alreadyActive: ${alreadyActive})`);

		if (!alreadyActive) {
			this._activeGameSessionInView = this.gameSessions[sessionIndex];
		}
		// Visually selects the corresponding tab
		this.viewManager.updatePlayerTabVisuals(
			sessionIndex,
			this.activeGameSessionInView.playerName ?? '',
			this.allSelectedCharacterIndexes
		);

		await this.activeGameSessionInView.becameActiveSession(alreadyActive);
		return true;
	};

	private endAllExistingGameSessions = async (): Promise<void> => {
		if (!this.gameSessions || this.gameSessions.length < 1) {
			return; // No GameSessions to end
		}

		const endSessionTasks: Promise<boolean>[] = [];
		for (let i = this.gameSessions.length - 1; i >= 0; i--) {
			endSessionTasks.push(this.tryEndGameSessionAtIndex(i, true));
			// Small delay between ending each session leads to fewer failed attempts to update db on device disconnect
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		const endSessionResults: boolean[] = await Promise.all(endSessionTasks);
		if (endSessionResults.includes(false)) {
			Log.error(`Unable to end all GameSessions - tryEndGameSessionAtIndex() failed for at least one session`);
			return;
		}

		Log.log('Successsfully ended all GameSessions');
	};

	/**
	 * Ends any GameSessions that were created but did not join a game
	 * e.g. This can happen if the host starts a game while someone is in the process of adding a player/session
	 */
	private endGameSessionsThatHaveNotJoined = async (
		allowNoSessions?: boolean,
		continueWhileResettingApp?: boolean
	): Promise<void> => {
		let sessionEnded: boolean;
		// Keep looping through gameSessions and ending any that have not joined a game
		//	until none are found. Done this way because tryEndGameSessionAtIndex manipulates the
		//	gameSessions array, so a single for loop could lead to wrong/invalid indexes being removed
		do {
			sessionEnded = false;
			for (let i = 0; i < this.gameSessions.length; i++) {
				if (!this.gameSessions[i].hasJoinedGame) {
					if (await this.tryEndGameSessionAtIndex(i, allowNoSessions)) {
						sessionEnded = true;
					}
					break;
				}
			}
		} while (sessionEnded && (continueWhileResettingApp === true || !this.resettingApp));
	};

	private tryAddGameSession = async (desiredSessionIndex?: number, playerName?: string): Promise<boolean> => {
		if (!this.gameSessions || this.gameSessions.length >= App.MaxGameSessions) {
			// Max number of players already joined on this device
			this.viewManager.showInfoPopupDialog('Error', 'No more players allowed on this device.', null);
			return false;
		}

		desiredSessionIndex ??= this.gameSessions.length; // default value

		let newSession: GameSession = new GameSession(
			this,
			desiredSessionIndex,
			this.viewManager,
			this.cookieHelper,
			playerName
		);

		this.gameSessions.push(newSession);
		Log.log(`Added new GameSession ${desiredSessionIndex} (current count: ${this.gameSessions.length})`);

		await newSession.connectToGame();

		newSession.onGameSessionEnded.subscribe(this.onGameSessionEnded);

		this.updateSessionsCookie();

		this.viewManager.setupJoinedPlayersList(this.allJoinedPlayerNames, this._sessionCount);

		return true;
	};

	private tryEndGameSessionAtIndex = async (
		sessionIndex: number,
		allowNoSessions: boolean = false
	): Promise<boolean> => {
		if (1 >= this.gameSessions.length && !allowNoSessions) {
			Log.error("endGameSessionAtIndex: Can't end GameSession - would result in no sessions");
			return false;
		}
		if (0 > sessionIndex || sessionIndex >= this.gameSessions.length) {
			Log.error(`endGameSessionAtIndex called with invalid playerIndex: ${sessionIndex}`);
			return false;
		}

		Log.log(`Ending GameSession at playerIndex: ${sessionIndex}`);

		try {
			// End session/close connection
			await this.gameSessions[sessionIndex].endSession();
		} catch (e) {
			Log.error(`Error while trying to end GameSession ${sessionIndex} - ${e}`);
			return false;
		}

		return true;
	};

	private onAllGameSessionsEnded = async (): Promise<void> => {
		// All game sessions ended - clear all relavant cookies
		this.cookieHelper.clearJoinCode();
		for (let i = 0; i < App.MaxGameSessions; i++) {
			this.cookieHelper.clearDeviceIdForSessionIndex(i);
			// Also hide all players tabs - sometimes some can be left visible if all sessions are
			// ended simultaneously due to session numbers changing mid-sessions-ending
			this.viewManager.setPlayerTabShowing(i, false);
		}
		// Reset and go back to initial join view
		// (May already be in process of resetting if all sessions are ended from resetApp())
		if (!this.resettingApp) {
			return this.resetApp();
		}
	};

	private onGameSessionEnded = async (sessionThatEnded: GameSession): Promise<void> => {
		Log.log(`GameSession ${sessionThatEnded.sessionIndex} was ended (connection closed)`);

		// Remove closed session from gameSessions array and clear subscription
		// let closedSession: GameSession = this.gameSessions.splice(sessionIndex, 1)[0];
		this.gameSessions.splice(this.gameSessions.indexOf(sessionThatEnded), 1);
		sessionThatEnded.onGameSessionEnded.unsubscribe(this.onGameSessionEnded);

		// Ensure GameSession indexes reflect their current positions within the gameSessions array
		for (let i = 0; i < this.gameSessions.length; i++) {
			this.gameSessions[i].changeSessionIndex(i);
		}

		if (0 < this.gameSessions.length) {
			try {
				// Switch to the most recent existing GameSession in case the activeGameSessionInView was the removed one
				await this.trySwitchActiveGameSession(this.gameSessions.length - 1);
			} catch (e) {
				Log.error(`Error trying to switch active game session in onGameSessionEnded - ${e}`);
			}
		}

		this.updateSessionsCookie();

		if (this.gameSessions.length > 0) {
			// Player removed, show WaitOrAddPlayers view with updated player names
			this.viewManager.setupJoinedPlayersList(this.allJoinedPlayerNames, this._sessionCount);
		} else {
			return this.onAllGameSessionsEnded();
		}
	};

	private onWriterCharacterSelectScrollEnd = (selectedIndex: number): void => {
		if (!this.activeGameSessionInView.isNonCharacterPlayer) {
			Log.debug('onWriterCharacterSelectScrollEnd, but active session is not a writer, so doing nothing');
			return;
		}
		this.writerButtonsScrolling = false;
		if (this.activeGameSessionInView.characterWritingData !== undefined) {
			this.activeGameSessionInView.characterWritingData.setSelectedCharacterIndex(selectedIndex);
		}
		this.viewManager.updateWriterTextInputSelectionVisuals(this.activeGameSessionInView.characterWritingData);
	};

	private updateSessionsCookie(): void {
		let list: [number, string][] = this.gameSessions
			.filter((s) => s.hasJoinedGame)
			.map((s) => [s.sessionIndex, s.playerName ?? '']);
		this.cookieHelper.setSessionsList(list);
	}

	private buttonPressVibration() {
		// window.navigator.vibrate(50);
	}

	/**
	 * Maps from UI event name + deviceId to whether it is currently being awaited.
	 * The addition of `deviceId` allows one session to use button while another is still pending.
	 * E.g. P1 chooses a character then P2 can still select one while server has not replied to P1.
	 */
	private uiEventsAwaiting: Map<string, boolean> = new Map<string, boolean>();

	// Protection from button spamming - either awaits the given funcToAwait, or skips the
	//	function call if we're still awaiting a previous button press with the same uiEventName
	private awaitButtonPressIfNotAlready = async (
		uiEventName: string,
		pressedElement: HTMLElement | null,
		deviceId: DeviceId,
		funcToAwait: () => Promise<void>
	) => {
		const key = `${uiEventName}-${deviceId}`;
		if (this.uiEventsAwaiting.get(key) ?? false) {
			Log.warn(`Skipping button press for '${uiEventName}' for device ${deviceId} (already awaited)`);
			return;
		}
		this.uiEventsAwaiting.set(key, true);
		// Apply disabledFromCode style to the element that was pressed
		//	to give players some visual feedback while awaiting
		pressedElement?.classList.add('disabledFromCode');
		try {
			await funcToAwait();
		} catch (e) {
			throw e;
		} finally {
			this.uiEventsAwaiting.set(key, false);
			pressedElement?.classList.remove('disabledFromCode');
		}
	};

	private onVisibilityChange(): void {
		Log.log('onVisibilityChange():', document.visibilityState);
	}

	private onBlur(evt: FocusEvent): void {
		Log.log('onBlur():', evt);
	}

	private onFocus(evt: FocusEvent): void {
		Log.log('onFocus():', evt);
	}

	/**
	 * Called before unload -- specifically we're using to intercept the "Reload" button.
	 */
	/**
	private onBeforeUnload(evt: BeforeUnloadEvent): string {
		Log.log('onBeforeUnload():', evt);

		// Prevent unload
		evt.preventDefault();

		// Ideally we could customise text shown 'Do you really wish to leave (leave/reload) or simply reconnect (stay/cancel)'
		// Sadly cannot seem to customise the text and using dialogs in this event is banned nowadays.
		// const answer = window.confirm('Do you wish to leave (OK) or reconnect (Cancel)?');
		// Log.log('answer:', answer);

		// TODO-20230224 Set a repeating timer to see whether canceled and try a ping?
		return (evt.returnValue = 'Do you really wish to leave (leave/reload) or simply reconnect (stay/cancel)');
	}
	*/

	/**
	 * Page is being unloaded.  Probably only fired on desktop browsers but might happen on any when reload occurring.
	 */
	/**
	private onUnload(evt: Event): void {
		Log.log('onUnload():', evt);
	}
	*/
}

const Log = LogFactory.build(App.name);

// @ts-ignore // TODO: Fix this non-reference
window.app = new App();
